/**
 * Branded types for numbers constrained to the [0, 1] range.
 * Used for scores, weights, percentages, and other normalized values.
 * @example
 * ```typescript
 * // Creating normalized values with validation
 * const score = NormalizedScore.create(0.85); // Valid
 * const weight = NormalizedWeight.create(0.5); // Valid
 * // These will throw at runtime:
 * NormalizedScore.create(1.5);  // Error: out of range
 * NormalizedWeight.create(-0.1); // Error: out of range
 * // Type-safe usage - compiler knows the constraint
 * function calculateWeightedScore(
 *   score: NormalizedScore,
 *   weight: NormalizedWeight
 * ): NormalizedScore {
 *   return NormalizedScore.create(score * weight);
 * }
 * ```
 */

import { z } from "zod";
import { InvalidNormalizedValueError } from "../errors/invalid-normalized-value.error.js";

declare const NormalizedValueBrand: unique symbol;
declare const NormalizedScoreBrand: unique symbol;

export type NormalizedScore = number & {
  readonly [NormalizedValueBrand]: true;
  readonly [NormalizedScoreBrand]: true;
};

function isInUnitRange(value: number): boolean {
  return typeof value === "number" && !isNaN(value) && value >= 0 && value <= 1;
}

export const NormalizedScore = {
  /**
   * Create a NormalizedScore from a number, with validation.
   * @param value - Number to convert (must be in [0, 1] range)
   * @returns NormalizedScore branded value
   * @throws InvalidNormalizedValueError if value is out of range
   */
  create(value: number): NormalizedScore {
    if (!isInUnitRange(value)) {
      // eslint-disable-next-line planta-v2/no-literal-error -- Dynamic error message with type name
      throw new InvalidNormalizedValueError("NormalizedScore", value);
    }
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Required for branded type construction
    return value as NormalizedScore;
  },

  unsafeCreate(value: number): NormalizedScore {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Required for branded type construction
    return value as NormalizedScore;
  },

  isValidRange(value: number): boolean {
    return isInUnitRange(value);
  },

  tryCreate(value: number): NormalizedScore | undefined {
    if (!isInUnitRange(value)) {
      return undefined;
    }
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Required for branded type construction
    return value as NormalizedScore;
  },
} as const;

/**
 * Zod schema for NormalizedScore.
 * Parses a plain `number` and brands it as a NormalizedScore, rejecting
 * values outside the [0, 1] range.
 */
export const NormalizedScoreSchema = z
  .number()
  .refine(NormalizedScore.isValidRange, {
    message: "NormalizedScore must be a number in the [0, 1] range",
  })
  .transform(NormalizedScore.unsafeCreate);

declare const NormalizedWeightBrand: unique symbol;

export type NormalizedWeight = number & {
  readonly [NormalizedValueBrand]: true;
  readonly [NormalizedWeightBrand]: true;
};

export const NormalizedWeight = {
  /**
   * Create a NormalizedWeight from a number, with validation.
   * @param value - Number to convert (must be in [0, 1] range)
   * @returns NormalizedWeight branded value
   * @throws InvalidNormalizedValueError if value is out of range
   */
  create(value: number): NormalizedWeight {
    if (!isInUnitRange(value)) {
      // eslint-disable-next-line planta-v2/no-literal-error -- Dynamic error message with type name
      throw new InvalidNormalizedValueError("NormalizedWeight", value);
    }
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Required for branded type construction
    return value as NormalizedWeight;
  },

  unsafeCreate(value: number): NormalizedWeight {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Required for branded type construction
    return value as NormalizedWeight;
  },

  isValidRange(value: number): boolean {
    return isInUnitRange(value);
  },

  tryCreate(value: number): NormalizedWeight | undefined {
    if (!isInUnitRange(value)) {
      return undefined;
    }
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Required for branded type construction
    return value as NormalizedWeight;
  },
} as const;
